import { useState, useRef, useEffect, useCallback } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions<T> {
  /** Initial value */
  initialValue: T;
  /** Function to save the value - receives the value to save */
  onSave: (value: T) => Promise<void>;
  /** Debounce delay in milliseconds (default: 1500) */
  debounceDelay?: number;
  /** Time to show "saved" status before returning to idle (default: 2000) */
  savedStatusDuration?: number;
  /** Time to show "error" status before returning to idle (default: 3000) */
  errorStatusDuration?: number;
}

interface UseAutoSaveReturn<T> {
  /** Current local value */
  value: T;
  /** Update local value and trigger debounced save */
  setValue: (newValue: T) => void;
  /** Current save status */
  saveStatus: AutoSaveStatus;
  /** Force immediate save (call on blur) */
  saveNow: () => Promise<void>;
  /** Check if value has unsaved changes */
  hasUnsavedChanges: boolean;
}

/**
 * Hook for auto-saving values with debouncing.
 *
 * Features:
 * - Debounced auto-save on value change
 * - Immediate save on blur (via saveNow)
 * - Save status tracking (idle, saving, saved, error)
 * - Skips save if value hasn't changed from last saved value
 * - Queues pending saves if user types during active save
 *
 * Note: Uses reference equality (===) for change detection.
 * For primitive types (string, number) this works correctly.
 * For objects/arrays, pass serialized values or ensure stable references.
 *
 * @example
 * ```tsx
 * const { value, setValue, saveStatus, saveNow } = useAutoSave({
 *   initialValue: intake.comments || '',
 *   onSave: async (comments) => {
 *     await updateIntakeRecord(intakeId, { comments });
 *   },
 * });
 *
 * return (
 *   <Textarea
 *     value={value}
 *     onChange={(e) => setValue(e.target.value)}
 *     onBlur={saveNow}
 *   />
 * );
 * ```
 */
export function useAutoSave<T>({
  initialValue,
  onSave,
  debounceDelay = 1500,
  savedStatusDuration = 2000,
  errorStatusDuration = 3000,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn<T> {
  const [localValue, setLocalValue] = useState<T>(initialValue);
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedValueRef = useRef<T>(initialValue);
  const isSavingRef = useRef(false);
  const pendingValueRef = useRef<T | null>(null);
  const currentSavePromiseRef = useRef<Promise<void> | null>(null);

  // Use ref for onSave to avoid stale closure issues (Claude suggestion)
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  // Sync local value when initialValue changes (e.g., from server refresh)
  useEffect(() => {
    setLocalValue(initialValue);
    lastSavedValueRef.current = initialValue;
  }, [initialValue]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  const performSave = useCallback(
    (valueToSave: T): Promise<void> => {
      const promise = (async () => {
        // Skip if value hasn't changed from last saved value
        if (valueToSave === lastSavedValueRef.current) {
          return;
        }

        // Queue value if already saving - will be processed after current save
        if (isSavingRef.current) {
          pendingValueRef.current = valueToSave;
          return;
        }

        try {
          isSavingRef.current = true;
          pendingValueRef.current = null;
          setSaveStatus('saving');
          await onSaveRef.current(valueToSave);
          lastSavedValueRef.current = valueToSave;
          setSaveStatus('saved');

          // Clear previous status timeout
          if (statusTimeoutRef.current) {
            clearTimeout(statusTimeoutRef.current);
          }
          // Reset to idle after duration
          statusTimeoutRef.current = setTimeout(() => {
            setSaveStatus('idle');
          }, savedStatusDuration);
        } catch (error) {
          console.error('Auto-save failed:', error);
          setSaveStatus('error');

          // Reset to idle after duration
          if (statusTimeoutRef.current) {
            clearTimeout(statusTimeoutRef.current);
          }
          statusTimeoutRef.current = setTimeout(() => {
            setSaveStatus('idle');
          }, errorStatusDuration);
        } finally {
          isSavingRef.current = false;
          currentSavePromiseRef.current = null;

          // Check if there's a pending value to save
          if (
            pendingValueRef.current !== null &&
            pendingValueRef.current !== lastSavedValueRef.current
          ) {
            const pending = pendingValueRef.current;
            pendingValueRef.current = null;
            currentSavePromiseRef.current = performSave(pending);
          }
        }
      })();
      currentSavePromiseRef.current = promise;
      return promise;
    },
    [savedStatusDuration, errorStatusDuration]
  );

  const setValue = useCallback(
    (newValue: T) => {
      setLocalValue(newValue);

      // Clear any existing debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new debounce timeout
      debounceTimeoutRef.current = setTimeout(() => {
        performSave(newValue);
      }, debounceDelay);
    },
    [debounceDelay, performSave]
  );

  const saveNow = useCallback(async () => {
    // Await any in-flight save before starting a new one
    if (currentSavePromiseRef.current) {
      await currentSavePromiseRef.current;
    }
    // Cancel pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    // Save immediately if changed
    if (localValue !== lastSavedValueRef.current) {
      await performSave(localValue);
    }
  }, [localValue, performSave]);

  const hasUnsavedChanges = localValue !== lastSavedValueRef.current;

  return {
    value: localValue,
    setValue,
    saveStatus,
    saveNow,
    hasUnsavedChanges,
  };
}
